import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../api/client';
import '../kits/KitPopup.css';
import './WildmicrPopup.css';
import MarketplacesField, { ALL_MARKETPLACES } from './MarketplacesField';
import AlternateIdsField from './AlternateIdsField';
import ProductDetailsField from './ProductDetailsField';
import SeedMixListField from './SeedMixListField';

const WildmicrPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [form, setForm] = useState({
        SKU: '', packName: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '',
        bagsPerBox: '', pack_size_oz: '', produce_per_shift: '', isActive: 1, niche_category_id: '',
        seedsID: '', seedslbperpack: '', marketplaces: ALL_MARKETPLACES,
    });

    const [brands,       setBrands]       = useState([]);
    const [niches,       setNiches]       = useState([]);
    const [allSeeds,     setAllSeeds]     = useState([]);
    const [selectedSeed, setSelectedSeed] = useState(null); // { seedsID, label }
    const [seedSearch,   setSeedSearch]   = useState('');
    const [seedDropOpen, setSeedDropOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    const seedWrapRef  = useRef(null);
    const seedInputRef = useRef(null);
    const [dropStyle, setDropStyle] = useState({});

    // Load meta once on open
    useEffect(() => {
        if (!isOpen) return;
        api.get('/api/options/brands')
            .then(r => setBrands(r.data.items || [])).catch(() => {});
        api.get('/api/options/niche_category')
            .then(r => setNiches(r.data.items || [])).catch(() => {});
        api.get('/api/options/seeds')
            .then(r => setAllSeeds(r.data.items || [])).catch(() => {});
    }, [isOpen]);

    // Populate form on edit
    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setForm({
                SKU:               editData.SKU               || '',
                packName:          editData.packName          || '',
                ASIN:              editData.ASIN              || '',
                FNSKU:             editData.FNSKU             || '',
                GTIN:              editData.GTIN              || '',
                brandID:           editData.brandID           || '',
                bagsPerBox:        editData.bagsPerBox        || '',
                pack_size_oz:      editData.pack_size_oz != null ? Number(editData.pack_size_oz) : '',
                produce_per_shift: editData.produce_per_shift || '',
                isActive:          editData.isActive != null ? editData.isActive : 1,
                niche_category_id: editData.niche_category_id || '',
                seedsID:           editData.seedsID           || '',
                seedslbperpack:    editData.seedslbperpack    || '',
                marketplaces:      editData.marketplaces != null ? editData.marketplaces : ALL_MARKETPLACES,
            });
        } else {
            setForm({ SKU: '', packName: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '',
                      bagsPerBox: '', pack_size_oz: '', produce_per_shift: '', isActive: 1,
                      niche_category_id: '', seedsID: '', seedslbperpack: '', marketplaces: ALL_MARKETPLACES });
            setSelectedSeed(null);
        }
        setSeedSearch(''); setSeedDropOpen(false); setError('');
    }, [isOpen, editData]);

    // Resolve seed label once allSeeds loaded
    useEffect(() => {
        if (!editData?.seedsID || !allSeeds.length) return;
        const s = allSeeds.find(x => String(x.seedsID) === String(editData.seedsID));
        setSelectedSeed(s ? { seedsID: s.seedsID, label: s.combinedSeedsName || s.seedsName || `#${s.seedsID}` } : null);
    }, [editData, allSeeds]);

    // Close dropdown on outside click
    useEffect(() => {
        const h = (e) => {
            if (seedWrapRef.current && !seedWrapRef.current.contains(e.target))
                setSeedDropOpen(false);
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    // Filtered seeds
    const filteredSeeds = useMemo(() => {
        if (!isOpen) return [];
        const q = seedSearch.trim().toLowerCase();
        return allSeeds.filter(s =>
            !q ||
            (s.combinedSeedsName || '').toLowerCase().includes(q) ||
            (s.seedsName         || '').toLowerCase().includes(q) ||
            (s.varietyName       || '').toLowerCase().includes(q)
        );
    }, [isOpen, allSeeds, seedSearch]);

    if (!isOpen) return null;

    const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

    const openDrop = () => {
        if (seedInputRef.current) {
            const r = seedInputRef.current.getBoundingClientRect();
            setDropStyle({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320) });
        }
        setSeedDropOpen(true);
    };

    const selectSeed = (s) => {
        const label = s.combinedSeedsName || s.seedsName || `#${s.seedsID}`;
        setSelectedSeed({ seedsID: s.seedsID, label });
        setForm(p => ({ ...p, seedsID: s.seedsID }));
        setSeedSearch('');
        setSeedDropOpen(false);
    };

    const clearSeed = () => {
        setSelectedSeed(null);
        setForm(p => ({ ...p, seedsID: '' }));
        setSeedSearch('');
        setTimeout(() => seedInputRef.current?.focus(), 50);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.SKU.trim()) { setError('SKU is required'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(`/api/wildmicr-popup/${editData.SKU}`, form);
            } else {
                await api.post('/api/wildmicr-popup', form);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup wm-popup">

                {/* Header */}
                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData ? `Edit — ${editData.packName || editData.SKU}` : 'Add Wildflower / Microgreen'}
                        </div>
                        <div className="kit-popup__subtitle">
                            {editData ? 'Update product details and seed' : 'Fill in product details and select seed'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose} aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <use xlinkHref="#closeBtn" />
                        </svg>
                    </button>
                </div>

                {/* Body — single scrollable panel */}
                <div className="wm-popup__body">

                    {/* Two-column field grid */}
                    <div className="wm-fields-grid">

                        <div className="kit-field">
                            <span className="kit-field__label">SKU *</span>
                            <input className="kit-field__input" value={form.SKU}
                                readOnly={!!editData} onChange={setField('SKU')}
                                placeholder="e.g. ORBWF_61_02" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Brand</span>
                            <select className="kit-field__input" value={form.brandID}
                                onChange={e => setForm(p => ({ ...p, brandID: e.target.value }))}>
                                <option value="">— Select brand —</option>
                                {brands.map(b => (
                                    <option key={b.brandID} value={b.brandID}>{b.brandName}</option>
                                ))}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Name</span>
                            <input className="kit-field__input" value={form.packName}
                                onChange={setField('packName')} placeholder="Product display name" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Niche</span>
                            <select className="kit-field__input" value={form.niche_category_id}
                                onChange={e => setForm(p => ({ ...p, niche_category_id: e.target.value }))}>
                                <option value="">— Select niche —</option>
                                {niches.map(n => (
                                    <option key={n.niche_category_id} value={n.niche_category_id}>{n.niche_name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">ASIN</span>
                            <input className="kit-field__input" value={form.ASIN}
                                onChange={setField('ASIN')} placeholder="Amazon ASIN" />
                        </div>

                        <div className="wm-fields-row">
                            <div className="kit-field" style={{ flex: 1 }}>
                                <span className="kit-field__label">Bags per Box</span>
                                <input className="kit-field__input" type="number" min="1"
                                    value={form.bagsPerBox} onChange={setField('bagsPerBox')} placeholder="e.g. 60" />
                            </div>
                            <div className="kit-field" style={{ flex: '0 0 110px' }}>
                                <span className="kit-field__label">Pack Size (oz)</span>
                                <input className="kit-field__input" type="number" min="0" step="0.01"
                                    value={form.pack_size_oz} onChange={setField('pack_size_oz')} placeholder="e.g. 1" />
                            </div>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">FNSKU</span>
                            <input className="kit-field__input" value={form.FNSKU}
                                onChange={setField('FNSKU')} placeholder="Amazon FNSKU" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">GTIN</span>
                            <input className="kit-field__input" value={form.GTIN}
                                onChange={setField('GTIN')} placeholder="Barcode / GTIN" />
                        </div>

                        <MarketplacesField
                            value={form.marketplaces}
                            onChange={v => setForm(p => ({ ...p, marketplaces: v }))}
                        />

                        {editData && <ProductDetailsField productType="wildmicr" sku={editData.SKU} />}
                        {editData && <AlternateIdsField productType="wildmicr" sku={editData.SKU} />}

                        <div className="wm-fields-row">
                            <div className="kit-field" style={{ flex: 1 }}>
                                <span className="kit-field__label">Produce per Shift</span>
                                <input className="kit-field__input" type="number" min="1"
                                    value={form.produce_per_shift} onChange={setField('produce_per_shift')} placeholder="e.g. 240" />
                            </div>
                            <div className="kit-field" style={{ flex: '0 0 100px' }}>
                                <span className="kit-field__label">Active</span>
                                <select className="kit-field__input" value={form.isActive}
                                    onChange={e => setForm(p => ({ ...p, isActive: parseInt(e.target.value) }))}>
                                    <option value={1}>Yes</option>
                                    <option value={0}>No</option>
                                </select>
                            </div>
                        </div>

                    </div>

                    {/* Seeds section — full width divider row */}
                    <div className="wm-seeds-section">
                        <div className="wm-seeds-label">
                            <i className="fas fa-leaf" />
                            Seeds
                        </div>

                        <div className="wm-seeds-row" ref={seedWrapRef}>
                            {/* Seed combobox */}
                            <div className="wm-seed-combo">
                                <i className="fas fa-search wm-seed-combo__icon" />
                                <input
                                    ref={seedInputRef}
                                    className={`wm-seed-combo__input${selectedSeed ? ' wm-seed-combo__input--selected' : ''}`}
                                    value={selectedSeed ? selectedSeed.label : seedSearch}
                                    readOnly={!!selectedSeed}
                                    placeholder="Search and select seed…"
                                    onChange={e => { setSeedSearch(e.target.value); setSeedDropOpen(true); }}
                                    onFocus={openDrop}
                                    onClick={() => { if (!selectedSeed) openDrop(); }}
                                />
                                {selectedSeed && (
                                    <button type="button" className="wm-seed-combo__clear"
                                        onClick={clearSeed} title="Clear seed">
                                        <i className="fas fa-times" />
                                    </button>
                                )}

                                {seedDropOpen && !selectedSeed && (
                                    <div className="set-comp__dropdown" style={dropStyle}>
                                        {filteredSeeds.length === 0 ? (
                                            <div className="set-comp__dropdown-empty">
                                                {seedSearch.trim() ? 'No seeds found' : 'Start typing to search…'}
                                            </div>
                                        ) : (
                                            filteredSeeds.slice(0, 100).map(s => (
                                                <div key={s.seedsID}
                                                    className="set-comp__dropdown-item"
                                                    onMouseDown={() => selectSeed(s)}>
                                                    <span className="set-comp__item-name">
                                                        {s.combinedSeedsName || s.seedsName}
                                                    </span>
                                                    <span className="set-comp__item-sku">#{s.seedsID}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Lbs per pack */}
                            <div className="wm-lbperpack">
                                <span className="kit-field__label">lb / pack</span>
                                <input
                                    className="kit-field__input"
                                    type="number" min="0" step="0.0001"
                                    value={form.seedslbperpack}
                                    onChange={setField('seedslbperpack')}
                                    placeholder="e.g. 0.0625"
                                />
                            </div>
                        </div>

                        <SeedMixListField seedsID={form.seedsID} />
                    </div>

                </div>

                {/* Footer */}
                <div className="kit-popup__footer">
                    <span className="kit-popup__error">{error && `⚠ ${error}`}</span>
                    <button className="kit-popup__submit" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Create Product')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default WildmicrPopup;
